import { useCallback, useEffect, useState, useMemo } from 'react';
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import { FormProvider, useForm } from 'react-hook-form';
import DescriptionTextarea from "../form-fields/DescriptionTextarea";
import apiHelper from "../../utils/apiHelper";
import { NotesEntityTypesEnum, NoteService } from "@propertelligent/client-api";
import NoteCard from './NoteCard';
import EmptyState from '../emptystate';
import notesFileIcon from "../../assets/notesFileIcon.svg";
import SearchFilterInput from '../form-fields/search-filter-input';
import { v4 as uuidv4 } from 'uuid';
import SimpleSpinner from '../../components/loader';

interface NotesValues {
  formatted?: Record<string, any> | null;
  id?: number;
  entityTypeId?: NotesEntityTypesEnum;
  entityId?: number;
  noteData?: string | null;
  modified?: string;
  modifiedById?: number;
  modifiedBy?: string | null;
}

interface AddNotesFormProps {
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  entityId: number;
  entityTypeId: number;
}

const AddNotesForm: React.FC<AddNotesFormProps> = ({
  isModalOpen,
  setModalOpen,
  entityId,
  entityTypeId
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchLoading, setIsFetchLoading] = useState(false);
  const [notesData, setNoteData] = useState<NotesValues[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [noteBeingEdited, setNoteBeingEdited] = useState<NotesValues | null>(null);
  const [noteBeingDeleted, setNoteBeingDeleted] = useState<number | null>(null);
  const formId = `NotesForm-${uuidv4()}`;


  const methods = useForm<NotesValues>({
    mode: "onChange",
    defaultValues: {
      id: null,
      entityTypeId: NotesEntityTypesEnum.Property,
      entityId: entityId,
      noteData: ""
    }
  });

  const fetchNoteData = useCallback(async () => {
    setIsFetchLoading(true);
    try {
      const response = await apiHelper(
        entityTypeId === NotesEntityTypesEnum.Property
          ? NoteService.getApiNotePropertylist
          : entityTypeId === NotesEntityTypesEnum.Unit
          ? NoteService.getApiNoteUnitlist
          : entityTypeId === NotesEntityTypesEnum.Tenant
          ? NoteService.getApiNoteTenantlist
          : entityTypeId === NotesEntityTypesEnum.ServiceRequest
          ? NoteService.getApiNoteServicerequestlist
          : entityTypeId === NotesEntityTypesEnum.Contractor
          ? NoteService.getApiNoteContractorlist
          : NoteService.getApiNotePropertylist,
        { showNotification: false },
        entityId
      );
      setNoteData(response.data.success);
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setIsFetchLoading(false);
    }
  }, [entityId]);

  const onSubmit = async (data: NotesValues) => {
    if(isLoading) return;
    setIsLoading(true);
    try {
      const requestData = {
        noteData: data.noteData,
        id: data.id || 0,
        entityTypeId,
        entityId,
      };

      await apiHelper(
        NoteService.postApiNote,
        {
          successMessage: "Note added successfully",
          showNotification: true
        },
        requestData
      );

      methods.reset();
      setModalOpen(false);
      await fetchNoteData();
    } catch (error) {
      console.error("Error submitting note:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateNote = async (data: NotesValues) => {
    setIsLoading(true);
    try {
      const requestData = {
        noteData: data.noteData,
        id: data.id || 0,
        entityTypeId: entityTypeId || 0,
        entityId: entityId,
      };

      await apiHelper(
        NoteService.putApiNote,
        {
          successMessage: "Note updated successfully",
          showNotification: true,
        },
        requestData
      );

      await fetchNoteData();
    } catch (error) {
      console.error("Error updating note:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteNote = async (id: number) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      await apiHelper(
        NoteService.deleteApiNote,
        {
          successMessage: "Note deleted successfully",
          showNotification: true
        },
        id
      );
      await fetchNoteData();
      // setModalOpen(false);
    } catch (error) {
      console.error("Error deleting note:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isModalOpen && entityId) {
      fetchNoteData();
    }
  }, [isModalOpen, entityId, fetchNoteData]);

  const handleClose = () => {
    setModalOpen(false);
    methods.reset(); // reset form state
    setNoteBeingEdited(null);
    setNoteBeingDeleted(null);
    setSearchTerm('');
  };


  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter notes based on searchTerm
  const filteredNotes = useMemo(() => {
    if (!searchTerm.trim()) return notesData;
    return notesData.filter((note) =>
      note.noteData?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [notesData, searchTerm]);

  const { formState: { isDirty, isValid } } = methods;

  return (
    <RightSideModal
      isOpen={isModalOpen}
      onClose={handleClose} // <-- updated
      title="Add Note"
      buttonlabel={isLoading ? "Saving..." : "Save"}
      formId={formId}
      maxWidth={550}
      isDirty={isDirty}
      isSubmitDisabled={isLoading || !isDirty || !isValid}
    >

      <Card className='no-bg'>
        <div className="row">
          <div className="col-md-12">
            <SearchFilterInput
              placeholder="Search Notes"
              searchTerm={searchTerm}
              handleSearch={handleSearch}
              extraClass="mb-3"
            />
          </div>
        </div>
        <FormProvider {...methods}>
          <form id={formId} onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="row">
              <div className="col-md-12">
                <DescriptionTextarea
                  label='Add Note'
                  name="noteData"
                  requiredMessage="Note is required"
                  rows={3}
                  required
                  disabled={isLoading}
                  placeholder="Enter Note"
                  validation={{
                    maxLength: {
                      value: 250,
                      message: "Note cannot be more than 250 characters"
                    }
                  }}
                />
              </div>
            </div>
          </form>
        </FormProvider>

        {isFetchLoading ? (
          <div className="d-flex justify-content-center align-items-center mt-5 mb-5">
            <SimpleSpinner />
          </div>
        ) : filteredNotes.length > 0 ? (
          filteredNotes.map((note) => ( // Removed 'index' from map arguments
            <NoteCard
              key={note.id} // Use the stable note.id as the key
              id={note.id}
              title={note.noteData}
              author={note.modifiedBy || "Unknown"}
              date={new Date(note?.modified).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || note.modified}
              entityTypeId={entityTypeId}
              entityId={entityId}
              onClose={handleClose}
              onUpdate={updateNote}
              onDelete={deleteNote}
              isLoading={isLoading}
            />
          ))
        ) : (
          <EmptyState
            icon={notesFileIcon}
            className='mt-4'
            isButton={false}
            heading={searchTerm ? "No Notes Match Your Search" : "No Notes Found"}
          />
        )}
      </Card>
    </RightSideModal>
  );
};

export default AddNotesForm;